    import { useEffect, useRef } from "react";
import {
    Activity, Shield, Terminal, Loader2, Clock, ChevronRight
} from "lucide-react";
import {
    PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis,
    Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import type { FwStats } from "./FirewallLogs";
import {
    getSeverityStyle, getActionStyle, formatTime, CustomTooltip, ACTION_COLORS
} from "./FirewallLogs";

interface OverviewTabProps {
    statsData: FwStats | null;
    timelineData: any[];
    liveLogs: any[];
    hours: string;
    setActiveTab: (tab: "overview" | "threats" | "traffic" | "users" | "apps" | "logs") => void;
    onRefreshStats?: () => void;
}

export default function OverviewTab({
    statsData,
    timelineData,
    liveLogs,
    hours,
    setActiveTab,
    onRefreshStats
}: OverviewTabProps) {
    const refreshRef = useRef(onRefreshStats);
    useEffect(() => {
        refreshRef.current = onRefreshStats;
    }, [onRefreshStats]);

    // Poll stats API every 10 seconds while on Overview tab
    useEffect(() => {
        const intervalId = setInterval(() => {
            refreshRef.current?.();
        }, 10000);

        return () => clearInterval(intervalId);
    }, []);
    const totalCount = statsData ? statsData.total : 0;
    const allowedCount = statsData ? statsData.allowed : 0;
    const blockedCount = statsData ? statsData.blocked : 0;
    const allowedPct = totalCount > 0 ? ((allowedCount / totalCount) * 100).toFixed(1) : "0";
    const blockedPct = totalCount > 0 ? ((blockedCount / totalCount) * 100).toFixed(1) : "0";
    const windowLabel = hours === "today" ? "Today's" : hours === "yesterday" ? "Yesterday's" : `Last ${hours}h`;

    return (
        <div className="animate-fade-in">
            {/* Visual cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {/* Total Events */}
                <div className="relative overflow-hidden bg-white dark:bg-slate-900/40 backdrop-blur-md border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 hover:scale-[1.02] hover:border-blue-500/30 transition-all duration-300 shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)] flex flex-col justify-between h-24">
                    <div className="absolute top-[-2px] left-0 right-0 h-[3px] bg-gradient-to-r from-blue-500 to-blue-400" />
                    <span className="text-[9px] text-blue-600 dark:text-blue-400 font-bold uppercase tracking-widest">TOTAL EVENTS</span>
                    <div className="text-2xl font-black text-gray-900 dark:text-white font-mono">{totalCount.toLocaleString()}</div>
                    <span className="text-[9px] text-slate-500 dark:text-gray-300 font-medium">{windowLabel} window</span>
                </div>

                {/* Allowed */}
                <div className="relative overflow-hidden bg-white dark:bg-slate-900/40 backdrop-blur-md border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 hover:scale-[1.02] hover:border-emerald-500/30 transition-all duration-300 shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)] flex flex-col justify-between h-24">
                    <div className="absolute top-[-2px] left-0 right-0 h-[3px] bg-gradient-to-r from-emerald-500 to-emerald-400" />
                    <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-widest">ALLOWED TRAFFIC</span>
                    <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono">{allowedCount.toLocaleString()}</div>
                    <span className="text-[9px] text-slate-500 dark:text-gray-300 font-medium">{allowedPct}% of total</span>
                </div>

                {/* Blocked */}
                <div className="relative overflow-hidden bg-white dark:bg-slate-900/40 backdrop-blur-md border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 hover:scale-[1.02] hover:border-red-500/30 transition-all duration-300 shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)] flex flex-col justify-between h-24">
                    <div className="absolute top-[-2px] left-0 right-0 h-[3px] bg-gradient-to-r from-red-500 to-red-400" />
                    <span className="text-[9px] text-red-600 dark:text-red-400 font-bold uppercase tracking-widest">BLOCKED REQUESTS</span>
                    <div className="text-2xl font-black text-red-600 dark:text-red-400 font-mono">{blockedCount.toLocaleString()}</div>
                    <span className="text-[9px] text-slate-500 dark:text-gray-300 font-medium">{blockedPct}% of total</span>
                </div>
            </div>

            {/* Chart structures */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
                {/* Traffic Timeline Chart */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 flex flex-col justify-between h-[300px] shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <h2 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest flex items-center gap-1.5 font-sans">
                            <Activity size={12} className="text-blue-500" /> Network Traffic Volume
                        </h2>
                        <span className="text-[10px] text-slate-500 font-mono font-bold uppercase">Real-time sync active</span>
                    </div>
                    <div className="flex-1 min-h-0 w-full relative">
                        {timelineData.length === 0 ? (
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-500 font-bold uppercase tracking-wider">No Timeline Data</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={timelineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="blockedGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#fb7185" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.15)" />
                                    <XAxis dataKey="label" minTickGap={25} tick={{ fill: '#94a3b8', fontSize: 8 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#94a3b8', fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Area type="monotone" name="Total Events" dataKey="total" stroke="#38bdf8" strokeWidth={1.5} fillOpacity={1} fill="url(#totalGrad)" />
                                    <Area type="monotone" name="Blocked Events" dataKey="blocked" stroke="#fb7185" strokeWidth={1.5} strokeDasharray="4 2" fillOpacity={1} fill="url(#blockedGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Actions Doughnut Chart */}
                <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 flex flex-col justify-between h-[300px] shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <h2 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest flex items-center gap-1.5 font-sans">
                            <Shield size={12} className="text-emerald-500" />  Policy Enforcement
                        </h2>
                    </div>
                    <div className="flex-1 flex flex-col md:flex-row lg:flex-col items-center justify-center gap-4 min-h-0">
                        <div className="h-[140px] w-[140px] relative flex items-center justify-center shrink-0">
                            {!statsData || !statsData.by_action || statsData.by_action.length === 0 ? (
                                <div className="text-xs text-slate-500 font-bold uppercase">No Actions Data</div>
                            ) : (
                                <>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={statsData.by_action}
                                                nameKey="label"
                                                dataKey="count"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={45}
                                                outerRadius={62}
                                                paddingAngle={2}
                                                stroke="none"
                                            >
                                                {statsData.by_action.map((_, index) => (
                                                    <Cell key={`cell-${index}`} fill={ACTION_COLORS[index % ACTION_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute text-center mt-[-2px] select-none pointer-events-none">
                                        <span className="text-lg font-black text-gray-900 dark:text-white font-mono">
                                            {totalCount > 0 ? ((allowedCount / totalCount) * 100).toFixed(0) : "0"}%
                                        </span>
                                        <span className="text-[7px] text-slate-500 dark:text-gray-300 block uppercase font-bold tracking-widest">Allowed</span>
                                    </div>
                                </>
                            )}
                        </div>
                        {statsData && statsData.by_action && statsData.by_action.length > 0 && (
                            <div className="flex-1 w-full flex flex-col gap-1.5 justify-center max-h-[120px] overflow-y-auto custom-scroll pr-1.5">
                                {statsData.by_action.map((item, index) => {
                                    const pct = totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : "0";
                                    return (
                                        <div key={index} className="flex items-center justify-between text-[10px] text-slate-600 dark:text-gray-300">
                                            <div className="flex items-center gap-1.5 truncate max-w-[120px] font-sans">
                                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: ACTION_COLORS[index % ACTION_COLORS.length] }} />
                                                <span className="capitalize truncate font-semibold">{item.label}</span>
                                            </div>
                                            <span className="font-mono text-gray-900 dark:text-gray-200 font-bold ml-2">{item.count.toLocaleString()} ({pct}%)</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Live Activity Feed (OVERVIEW INTEGRATION) */}
            <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)] mt-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-950/20 shrink-0">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                            <Terminal size={14} className="text-orange-500" />
                        </div>
                        <div>
                            <h2 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest">Recent Live Activity</h2>
                            <p className="text-[9px] text-slate-500 font-bold uppercase mt-0.5">Automated Event Stream</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 px-2.5 py-1 bg-emerald-500/5 border border-emerald-500/10 rounded-full">
                            <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]" />
                            <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-500/80 uppercase tracking-tighter">Live Sync Active</span>
                        </div>
                        <button
                            onClick={() => setActiveTab("logs")}
                            className="text-[9px] font-black text-slate-500 hover:text-orange-500 dark:text-slate-400 dark:hover:text-orange-500 uppercase tracking-widest transition-colors flex items-center gap-1 group"
                        >
                            View Detailed Logs <ChevronRight size={10} className="group-hover:translate-x-0.5 transition-transform" />
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto custom-scroll max-h-[380px]">
                    <table className="w-full table-fixed text-left border-collapse min-w-[900px]">
                        <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 uppercase tracking-widest text-[9px] font-black sticky top-0 z-20 border-b border-slate-200 dark:border-slate-800">
                            <tr>
                                <th className="w-[18%] px-4 py-3 border-r border-slate-200 dark:border-slate-800/50">Timestamp</th>
                                <th className="w-[12%] px-4 py-3 border-r border-slate-200 dark:border-slate-800/50">Severity</th>
                                <th className="w-[12%] px-4 py-3 border-r border-slate-200 dark:border-slate-800/50">Action</th>
                                <th className="w-[20%] px-4 py-3 border-r border-slate-200 dark:border-slate-800/50">Source</th>
                                <th className="w-[20%] px-4 py-3 border-r border-slate-200 dark:border-slate-800/50">Destination</th>
                                <th className="w-[18%] px-4 py-3">App / Service</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800/40">
                            {liveLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-3 text-slate-500">
                                            <Loader2 className="w-6 h-6 animate-spin text-orange-500/50" />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Awaiting incoming events...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                liveLogs.slice(0, 15).map((log, idx) => (
                                    <tr
                                        key={log.id || idx}
                                        className="group hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all border-l-2 border-transparent"
                                    >
                                        <td className="px-4 py-2.5 whitespace-nowrap overflow-hidden">
                                            <div className="flex items-center gap-2 truncate">
                                                <Clock size={10} className="text-slate-400 dark:text-slate-500 shrink-0" />
                                                <span className="text-[10px] text-slate-600 dark:text-slate-400 font-mono font-medium truncate">{formatTime(log.received_at)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap overflow-hidden">
                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border shadow-sm inline-block ${getSeverityStyle(log.severity)}`}>
                                                {log.severity || "—"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap overflow-hidden">
                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border shadow-sm inline-block ${getActionStyle(log.action)}`}>
                                                {log.action || "—"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap overflow-hidden">
                                            <div className="flex flex-col truncate" title={`${log.src_ip || ""} (${log.srccountry || ""})`}>
                                                <span className="text-[11px] text-cyan-600 dark:text-cyan-400 font-mono font-bold truncate">{log.src_ip || "—"}</span>
                                                <span className="text-[8px] text-slate-500 font-bold uppercase truncate">{log.srccountry || ""}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap overflow-hidden">
                                            <div className="flex flex-col truncate" title={`${log.dst_ip || ""} (${log.dstcountry || ""})`}>
                                                <span className="text-[11px] text-gray-900 dark:text-white font-mono font-bold truncate">{log.dst_ip || "—"}</span>
                                                <span className="text-[8px] text-slate-500 font-bold uppercase truncate">{log.dstcountry || ""}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200 font-bold text-[10px] whitespace-nowrap overflow-hidden">
                                            <span className="truncate block" title={log.app || log.service || ""}>
                                                {log.app || log.service || "—"}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
